import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import PrimaryButton from "../components/PrimaryButton";

const AUTH_TOKEN_STORAGE_KEY = "authToken";
const AUTH_ROLE_STORAGE_KEY = "authRole";

const normalizeRole = (role) => {
  const cleaned = String(role || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]/g, "_");

  if (cleaned === "SUPERADMIN") {
    return "SUPER_ADMIN";
  }
  if (cleaned === "TANNERYADMIN") {
    return "TANNERY_ADMIN";
  }

  return cleaned;
};

const getBaseApiUrl = () => {
  const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
  if (!baseApiUrl) {
    throw new Error("REACT_APP_BASE_API_URL is not configured.");
  }
  return baseApiUrl.replace(/\/+$/, "");
};

const getFriendlyErrorMessage = (statusCode, payload) => {
  if (statusCode === 401) {
    return "Your session has expired. Please login again.";
  }
  if (statusCode === 403) {
    return "You are not allowed to view logs.";
  }
  if (typeof payload?.message === "string" && payload.message.trim()) {
    return payload.message;
  }
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  return "Unable to load logs right now.";
};

const formatTimestamp = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
};

function LogsPage() {
  const navigate = useNavigate();
  const userRole = normalizeRole(localStorage.getItem(AUTH_ROLE_STORAGE_KEY));
  const isSuperAdmin = userRole === "SUPER_ADMIN";
  const isTanneryAdmin = userRole === "TANNERY_ADMIN";
  const isAllowedRole = isSuperAdmin || isTanneryAdmin;

  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const parseResponsePayload = async (response) => {
    const responseText = await response.text();
    if (!responseText) {
      return null;
    }

    try {
      return JSON.parse(responseText);
    } catch (error) {
      return responseText;
    }
  };

  const fetchLogs = useCallback(async () => {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
    if (!token) {
      navigate("/", { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const logsEndpoint = isSuperAdmin
        ? "/super-admin/logs"
        : "/tannery-admin/logs";
      const response = await fetch(`${getBaseApiUrl()}${logsEndpoint}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const payload = await parseResponsePayload(response);
      if (!response.ok) {
        throw new Error(getFriendlyErrorMessage(response.status, payload));
      }

      const logItems = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
        ? payload.data
        : [];
      setLogs(logItems);
      setCurrentPage(1);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load logs right now.");
    } finally {
      setIsLoading(false);
    }
  }, [isSuperAdmin, navigate]);

  useEffect(() => {
    if (!isAllowedRole) {
      return;
    }
    fetchLogs();
  }, [fetchLogs, isAllowedRole]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(logs.length / rowsPerPage)),
    [logs.length, rowsPerPage]
  );

  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return logs.slice(start, start + rowsPerPage);
  }, [currentPage, logs, rowsPerPage]);

  const pageStartIndex = logs.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const pageEndIndex = Math.min(currentPage * rowsPerPage, logs.length);

  if (!isAllowedRole) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="dashboard-card">
      <div className="dashboard-users-list-header">
        <h2>Logs</h2>
        <PrimaryButton onClick={fetchLogs}>
          {isLoading ? "Refreshing..." : "Refresh"}
        </PrimaryButton>
      </div>
      <p className="dashboard-card-subtitle">
        {isSuperAdmin
          ? "Audit logs for SuperAdmin actions across the system."
          : "Audit logs for your tannery activities."}
      </p>

      {errorMessage ? (
        <p className="dashboard-error-message" role="alert">
          {errorMessage}
        </p>
      ) : null}

      {isLoading ? <p>Loading logs...</p> : null}
      {!isLoading && logs.length === 0 ? <p>No logs found.</p> : null}

      {!isLoading && logs.length > 0 ? (
        <div>
          <div className="logs-pagination-toolbar">
            <p className="token-list-count">
              Showing {pageStartIndex}-{pageEndIndex} of {logs.length} logs
            </p>
            <div className="logs-pagination-controls">
              <label htmlFor="logs-rows-per-page" className="score-filter-label">
                Rows
              </label>
              <select
                id="logs-rows-per-page"
                className="score-filter-select"
                value={rowsPerPage}
                onChange={(event) => {
                  const nextSize = Number(event.target.value);
                  setRowsPerPage(nextSize);
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="dashboard-users-table-wrapper">
            <table className="dashboard-users-table">
              <thead>
                <tr>
                  <th scope="col">Time</th>
                  <th scope="col">User Role</th>
                  <th scope="col">Username</th>
                  <th scope="col">Tannery</th>
                  <th scope="col">Action</th>
                  <th scope="col">Method</th>
                  <th scope="col">Endpoint</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((logItem, index) => (
                  <tr key={`${logItem.timestamp || logItem.time || "log"}-${index}-${currentPage}`}>
                    <td>{formatTimestamp(logItem.time || logItem.timestamp)}</td>
                    <td>{logItem.user || "-"}</td>
                    <td>{logItem.username || "-"}</td>
                    <td>{logItem.tannery_name || "-"}</td>
                    <td>{logItem.action || "-"}</td>
                    <td>{logItem.method || "-"}</td>
                    <td>{logItem.endpoint || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="logs-pagination-toolbar">
            <div className="logs-pagination-controls">
              <PrimaryButton
                type="button"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </PrimaryButton>
              <span className="score-filter-label">
                Page {currentPage} of {totalPages}
              </span>
              <PrimaryButton
                type="button"
                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </PrimaryButton>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}

export default LogsPage;
